import {
  Stack,
  Typography,
  Button,
  Box,
  LinearProgress,
  IconButton,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useGetIntakeQuestionsQuery } from "src/api/services/intakeQuestionsApi";
import { MatchParam } from "src/constants/matches";
import { Column, IntakeQuestionOption } from "src/api/types";
import TruncatedText from "src/components/TruncatedText";
import HoverReveal from "src/components/HoverReveal";
import AddNewIntakeQuestionOption from "src/components/Modals/AddNewIntakeQuestionOption";
import { useManageModal } from "src/hooks/useManageModal";
import { DragIndicator, Edit } from "@mui/icons-material";
import { theme } from "src/assets/theme";
import { useIntakeDrawer } from "src/context/drawers/IntakesDrawerProvider/useIntakesDrawer";
import SortableDataTable from "src/components/SortableDataTable";
import { useDispatch } from "react-redux";
import { AppDispatch } from "src/api/store";
import {
  intakeQuestionOptionsApi,
  useBulkUpdateQuestionOptionsMutation,
  useGetIntakeQuestionOptionsQuery,
} from "src/api/services/intakeQuestionOptionsApi";

const useColumns = (): Column<IntakeQuestionOption>[] => {
  const { handleOpenUpdateIntakeQuestionOption } = useIntakeDrawer();

  return [
    {
      id: "drag",
      label: "",
      minWidth: 80,
      align: "center",
      orderable: false,
      format: (row) => row.id.toString(),
      render: () => <DragIndicator color="info" />,
    },
    {
      id: "edit",
      label: "Edit",
      minWidth: 80,
      align: "center",
      orderable: false,
      format: (row) => row.id.toString(),
      render: (row) => (
        <IconButton onClick={() => handleOpenUpdateIntakeQuestionOption(row)}>
          <Edit sx={{ color: theme.palette.secondary.main }} />
        </IconButton>
      ),
    },
    {
      id: "position",
      label: "Position",
      orderable: false,
      align: "center",
      format: (row) => row.position.toString(),
      render: (row) => <Typography>{row.position}</Typography>,
    },
    {
      id: "slug",
      label: "Slug",
      orderable: false,
      align: "center",
      format: (row) => row.slug,
      render: (row) => <Typography>{row.slug}</Typography>,
    },
    {
      id: "label",
      label: "Label",
      orderable: false,
      align: "center",
      format: (row) => row.label,
      render: (row) => <TruncatedText text={row.label} maxLength={20} />,
    },
    {
      id: "value",
      label: "Value",

      orderable: false,
      align: "center",
      format: (row) => row.value,
      render: (row) => <TruncatedText text={row.value} maxLength={20} />,
    },
    {
      id: "next_question",
      label: "Next Question",
      orderable: false,
      align: "center",
      format: (row) => row.next_question?.slug ?? "N/A",
      render: (row) => {
        if (row.next_question == null) {
          return <Typography>N/A</Typography>;
        }
        return (
          <HoverReveal
            label={row.next_question?.slug ?? "N/A"}
            value={row.next_question?.title}
          />
        );
      },
    },
  ];
};

const IntakeQuestionOptions = () => {
  const [open, { handleClose, handleOpen }] = useManageModal();
  const { intakeId } = useParams<MatchParam<"INTAKE_ID">>();
  const { questionId } = useParams<MatchParam<"QUESTION_ID">>();
  const { question } = useGetIntakeQuestionsQuery(
    intakeId != null ? Number(intakeId) : skipToken,
    {
      selectFromResult: ({ data }) => ({
        question: data?.find((q) => q.id === Number(questionId)),
      }),
    },
  );

  const { data: intakes = [], isFetching } = useGetIntakeQuestionOptionsQuery(
    intakeId != null && questionId != null
      ? { intakeId: Number(intakeId), questionId: Number(questionId) }
      : skipToken,
  );
  const [updateQuestionOptions] = useBulkUpdateQuestionOptionsMutation();
  const columns = useColumns();
  const height = useMemo(() => window.innerHeight - 104 - 64 - 34, []);
  const dispatch = useDispatch<AppDispatch>();

  const handleReorder = useCallback(
    (reorderedOptions: IntakeQuestionOption[]) => {
      dispatch(
        intakeQuestionOptionsApi.util.updateQueryData(
          "getIntakeQuestionOptions",
          { intakeId: Number(intakeId), questionId: Number(questionId) },
          (draft) => {
            const reordered = reorderedOptions.map(
              (q) => draft.find((d) => d.id === q.id)!,
            );
            draft.splice(0, draft.length, ...reordered);
          },
        ),
      );

      updateQuestionOptions({
        intakeId: Number(intakeId),
        questionId: Number(questionId),
        body: reorderedOptions.map((o, idx) => ({
          option_id: o.id,
          sort_order: idx,
        })),
      });
    },
    [dispatch, intakeId, questionId, updateQuestionOptions],
  );

  return (
    <Stack gap={2} sx={{ width: "100%", minWidth: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" gap={1}>
          <Typography variant="h5">Options:</Typography>
          <Typography variant="h5" fontStyle="italic">
            {question?.title}
          </Typography>
        </Stack>
        <Button onClick={handleOpen}>+ Add Option</Button>
      </Stack>
      <Stack gap={0} sx={{ width: "100%", minWidth: 0 }}>
        <Box height={4}>{isFetching ? <LinearProgress /> : null}</Box>
        <SortableDataTable
          data={intakes}
          columns={columns}
          height={height}
          primaryKey="id"
          onReorder={handleReorder}
        />
      </Stack>
      {intakeId && questionId ? (
        <AddNewIntakeQuestionOption
          open={open}
          onClose={handleClose}
          intakeQuestionId={Number(questionId)}
          intakeId={Number(intakeId)}
        />
      ) : null}
    </Stack>
  );
};

export default IntakeQuestionOptions;
